/**
 * Ranqueamento do matching (seção 7.4): complementaridade de habilidades,
 * sobreposição de fuso, idioma em comum, reputação e disponibilidade.
 * Os pesos são o ponto de partida para experimentos, não verdades.
 */
export const MATCH_WEIGHTS = {
  skills: 0.4,
  timezone: 0.2,
  language: 0.2,
  reputation: 0.15,
  available: 0.05,
} as const;

export interface MatchProfile {
  skills: Record<string, number>; // slug → proficiência (1–5)
  languages: string[];
  timezone: string | null;
  reputation: number;
  available: boolean;
}

export interface MatchBreakdown {
  score: number;
  skills: number;
  timezone: number;
  language: number;
  reputation: number;
  available: number;
  /** Habilidades do candidato que respondem ao pedido / complementam as minhas. */
  matchedSkills: string[];
}

/** Diferença de fuso em minutos no instante `at`. Devolve null para fusos inválidos. */
export function timezoneOffsetMinutes(timeZone: string, at = new Date()): number | null {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value;
    if (!part || part === "GMT") return 0;
    const m = /GMT([+-])(\d{2}):(\d{2})/.exec(part);
    if (!m) return null;
    const minutes = Number(m[2]) * 60 + Number(m[3]);
    return m[1] === "-" ? -minutes : minutes;
  } catch {
    return null;
  }
}

export function timezoneOverlap(a: string | null, b: string | null, at = new Date()): number {
  if (!a || !b) return 0.5; // desconhecido: neutro
  const oa = timezoneOffsetMinutes(a, at);
  const ob = timezoneOffsetMinutes(b, at);
  if (oa === null || ob === null) return 0.5;
  const diffHours = Math.min(Math.abs(oa - ob) / 60, 24 - Math.abs(oa - ob) / 60);
  return Math.max(0, 1 - diffHours / 12);
}

export function scoreMatch(
  me: MatchProfile,
  candidate: MatchProfile,
  wantedSkills: readonly string[] = [],
  at = new Date(),
): MatchBreakdown {
  // Com pedido explícito ("preciso de alguém que saiba X"), mede cobertura do pedido;
  // sem pedido, mede o que o candidato sabe e eu não sei (complementaridade).
  const matchedSkills: string[] = [];
  let skills: number;
  if (wantedSkills.length > 0) {
    let sum = 0;
    for (const slug of wantedSkills) {
      const p = candidate.skills[slug] ?? 0;
      if (p > 0) matchedSkills.push(slug);
      sum += p / 5;
    }
    skills = sum / wantedSkills.length;
  } else {
    const entries = Object.entries(candidate.skills);
    let sum = 0;
    for (const [slug, p] of entries) {
      const mine = me.skills[slug] ?? 0;
      if (p > mine) {
        sum += (p - mine) / 5;
        matchedSkills.push(slug);
      }
    }
    skills = entries.length ? Math.min(1, sum / 3) : 0;
  }

  const timezone = timezoneOverlap(me.timezone, candidate.timezone, at);
  const myLangs = new Set(me.languages.map((l) => l.toLowerCase()));
  const language = candidate.languages.some((l) => myLangs.has(l.toLowerCase())) ? 1 : myLangs.size ? 0 : 0.5;
  // Reputação em escala logarítmica para não deixar poucos perfis dominarem.
  const reputation = Math.min(1, Math.log10(1 + Math.max(0, candidate.reputation)) / 3);
  const available = candidate.available ? 1 : 0;

  const score =
    MATCH_WEIGHTS.skills * skills +
    MATCH_WEIGHTS.timezone * timezone +
    MATCH_WEIGHTS.language * language +
    MATCH_WEIGHTS.reputation * reputation +
    MATCH_WEIGHTS.available * available;

  return {
    score: Math.round(score * 1000) / 1000,
    skills,
    timezone,
    language,
    reputation,
    available,
    matchedSkills,
  };
}
